import { useState } from "react";
import type { EngineOutput } from "../../domain/types";
import { buildExportReport, exportReportToJson, exportReportToMarkdown } from "../../domain/exportReport";
import "./ExportPanel.css";

export interface ExportPanelProps {
  output: EngineOutput;
}

type ExportState = "idle" | "generating" | "error";

/**
 * Exportación de evidencia. Nunca incluye el payload original y siempre descarga como
 * archivo adjunto (equivalente en cliente de Content-Disposition: attachment), no como
 * navegación ni innerHTML.
 */
export function ExportPanel({ output }: ExportPanelProps) {
  const [state, setState] = useState<ExportState>("idle");

  function download(content: string, filename: string, mime: string): void {
    try {
      setState("generating");
      const blob = new Blob([content], { type: mime });
      const url = URL.createObjectURL(blob);
      const anchor = document.createElement("a");
      anchor.href = url;
      anchor.download = filename;
      anchor.rel = "noopener";
      document.body.appendChild(anchor);
      anchor.click();
      anchor.remove();
      URL.revokeObjectURL(url);
      setState("idle");
    } catch {
      setState("error");
    }
  }

  function handleExport(format: "json" | "markdown"): void {
    const report = buildExportReport(output);
    if (format === "json") {
      download(exportReportToJson(report), `${output.runId}.json`, "application/json");
    } else {
      download(exportReportToMarkdown(report), `${output.runId}.md`, "text/markdown");
    }
  }

  return (
    <div className="export-panel">
      <p className="export-panel__label" id="export-panel-label">
        Exportar evidencia (sin payload original)
      </p>
      <div className="export-panel__actions" aria-labelledby="export-panel-label">
        <button
          type="button"
          className="btn btn--ghost"
          onClick={() => handleExport("json")}
          disabled={state === "generating"}
        >
          Descargar JSON
        </button>
        <button
          type="button"
          className="btn btn--ghost"
          onClick={() => handleExport("markdown")}
          disabled={state === "generating"}
        >
          Descargar Markdown
        </button>
      </div>
      {state === "error" && (
        <p role="alert" className="export-panel__error">
          No se pudo generar el archivo. Puedes reintentar; el resultado sigue disponible aquí.
        </p>
      )}
    </div>
  );
}
